export const SINGLE_MAX_TRACKS = 2;
export const COMPANION_ARTIST_SLUG = "truevined";
export const AUDIO_ANALYSIS_VERSION = 1;

export interface CompanionPlaylistSettings {
  enabled: boolean;
  length: number;
  includeInstrumentals: boolean;
  includeAlternateVersions: boolean;
  nameTemplate: string;
  descriptionTemplate: string;
  albumNameTemplate: string;
  moodLength: number;
  moodNameTemplate: string;
}

export const COMPANION_PLAYLIST_DEFAULTS: CompanionPlaylistSettings = {
  enabled: false,
  length: 30,
  includeInstrumentals: false,
  includeAlternateVersions: true,
  nameTemplate: "{track} and more by {artist}",
  descriptionTemplate:
    "Starts with {track}, then the {artist} songs closest to it in tempo, key and energy. Built from truevined.com and refreshed with every release.",
  albumNameTemplate: "{album} and more by {artist}",
  moodLength: 30,
  moodNameTemplate: "{mood} by {artist}",
};

export const COMPANION_LENGTH_MIN = 3;
export const COMPANION_LENGTH_MAX = 50;
export const MOOD_LENGTH_MIN = 5;
export const MOOD_LENGTH_MAX = 50;

const clampInt = (value: unknown, min: number, max: number, fallback: number) => {
  const number = typeof value === "number" && Number.isFinite(value) ? Math.round(value) : fallback;
  return Math.min(Math.max(number, min), max);
};

const stringOr = (value: unknown, fallback: string, max: number) => {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, max) : fallback;
};

export function normalizeCompanionSettings(value: unknown): CompanionPlaylistSettings {
  const stored = value && typeof value === "object" ? (value as Partial<CompanionPlaylistSettings>) : {};
  return {
    enabled: typeof stored.enabled === "boolean" ? stored.enabled : COMPANION_PLAYLIST_DEFAULTS.enabled,
    length: clampInt(stored.length, COMPANION_LENGTH_MIN, COMPANION_LENGTH_MAX, COMPANION_PLAYLIST_DEFAULTS.length),
    includeInstrumentals:
      typeof stored.includeInstrumentals === "boolean" ? stored.includeInstrumentals : COMPANION_PLAYLIST_DEFAULTS.includeInstrumentals,
    includeAlternateVersions:
      typeof stored.includeAlternateVersions === "boolean"
        ? stored.includeAlternateVersions
        : COMPANION_PLAYLIST_DEFAULTS.includeAlternateVersions,
    nameTemplate: stringOr(stored.nameTemplate, COMPANION_PLAYLIST_DEFAULTS.nameTemplate, 80),
    descriptionTemplate: stringOr(stored.descriptionTemplate, COMPANION_PLAYLIST_DEFAULTS.descriptionTemplate, 280),
    albumNameTemplate: stringOr(stored.albumNameTemplate, COMPANION_PLAYLIST_DEFAULTS.albumNameTemplate, 80),
    moodLength: clampInt(stored.moodLength, MOOD_LENGTH_MIN, MOOD_LENGTH_MAX, COMPANION_PLAYLIST_DEFAULTS.moodLength),
    moodNameTemplate: stringOr(stored.moodNameTemplate, COMPANION_PLAYLIST_DEFAULTS.moodNameTemplate, 80),
  };
}

export type CompanionPlaylistKind = "single" | "album" | "mood";

export function companionReleaseKind(release: { totalTracks: number }): "single" | "album" {
  return release.totalTracks <= SINGLE_MAX_TRACKS ? "single" : "album";
}

export function isCompanionEligible(release: { artistSlug: string; totalTracks: number }, kind?: "single" | "album"): boolean {
  if (release.artistSlug !== COMPANION_ARTIST_SLUG) return false;
  return kind ? companionReleaseKind(release) === kind : true;
}

export function renderCompanionTemplate(
  template: string,
  values: { track?: string; artist: string; album?: string; mood?: string },
): string {
  return template
    .replace(/\{track\}/g, values.track ?? "")
    .replace(/\{album\}/g, values.album ?? "")
    .replace(/\{mood\}/g, values.mood ?? "")
    .replace(/\{artist\}/g, values.artist)
    .replace(/\s+/g, " ")
    .trim();
}

export const PITCH_CLASSES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"] as const;

export type KeyMode = "major" | "minor";

const CAMELOT_MAJOR: Record<number, number> = { 0: 8, 1: 3, 2: 10, 3: 5, 4: 12, 5: 7, 6: 2, 7: 9, 8: 4, 9: 11, 10: 6, 11: 1 };
const CAMELOT_MINOR: Record<number, number> = { 0: 5, 1: 12, 2: 7, 3: 2, 4: 9, 5: 4, 6: 11, 7: 6, 8: 1, 9: 8, 10: 3, 11: 10 };

export function camelotFor(pitchClass: number, mode: KeyMode): string {
  const number = mode === "major" ? CAMELOT_MAJOR[pitchClass] : CAMELOT_MINOR[pitchClass];
  return `${number}${mode === "major" ? "B" : "A"}`;
}

export function keyLabel(pitchClass: number | null, mode: KeyMode | null): string {
  if (pitchClass === null || mode === null) return "Unknown key";
  return `${PITCH_CLASSES[pitchClass]} ${mode}`;
}

export function keyDistance(a: { pitchClass: number; mode: KeyMode }, b: { pitchClass: number; mode: KeyMode }): number {
  const numberA = a.mode === "major" ? CAMELOT_MAJOR[a.pitchClass] : CAMELOT_MINOR[a.pitchClass];
  const numberB = b.mode === "major" ? CAMELOT_MAJOR[b.pitchClass] : CAMELOT_MINOR[b.pitchClass];
  const wheelSteps = Math.min(Math.abs(numberA - numberB), 12 - Math.abs(numberA - numberB));
  if (a.mode === b.mode) return wheelSteps === 0 ? 0 : wheelSteps === 1 ? 1 : wheelSteps === 2 ? 2 : 3;
  return wheelSteps === 0 ? 1 : wheelSteps === 1 ? 2 : 3;
}

export function tempoDistance(a: number, b: number): number {
  if (!(a > 0) || !(b > 0)) return 1;
  const ratios = [1, 2, 0.5];
  const closest = Math.min(...ratios.map((ratio) => Math.abs(Math.log2((a * ratio) / b))));
  return Math.min(closest / Math.log2(1.35), 1);
}

export interface AudioFeatureVector {
  bpm: number;
  keyPitchClass: number | null;
  mode: KeyMode | null;
  energy: number;
  brightness: number;
  loudnessDb?: number | null;
}

export function hasUsableFeatures(features: Partial<AudioFeatureVector> | null | undefined): features is AudioFeatureVector {
  return Boolean(features && typeof features.bpm === "number" && features.bpm > 0);
}

export function similarityScore(seed: AudioFeatureVector, other: AudioFeatureVector): number {
  const tempo = 1 - tempoDistance(seed.bpm, other.bpm);
  const key =
    seed.keyPitchClass !== null && seed.mode !== null && other.keyPitchClass !== null && other.mode !== null
      ? 1 - keyDistance({ pitchClass: seed.keyPitchClass, mode: seed.mode }, { pitchClass: other.keyPitchClass, mode: other.mode }) / 3
      : 0.5;
  const energy = 1 - Math.min(Math.abs(seed.energy - other.energy), 1);
  const brightness = 1 - Math.min(Math.abs(seed.brightness - other.brightness), 1);
  const loudness =
    typeof seed.loudnessDb === "number" && typeof other.loudnessDb === "number"
      ? 1 - Math.min(Math.abs(seed.loudnessDb - other.loudnessDb) / 12, 1)
      : 0.5;
  return tempo * 0.35 + key * 0.25 + energy * 0.2 + brightness * 0.1 + loudness * 0.1;
}

const VARIANT_MARKERS =
  /\s*(?:-\s*|[(\[]\s*)[^()\[\]-]*?\b(?:instrumental|remix|acoustic|live|radio edit|edit|extended|sped up|slowed|version|mix|demo|reprise|acapella|a cappella)\b[^)\]]*[)\]]?\s*$/i;
const FEATURE_MARKERS = /\s*[(\[]?\s*(?:feat\.?|ft\.?|featuring|with)\s+[^)\]]*[)\]]?\s*$/i;

export function baseTitle(name: string): string {
  let title = name.toLowerCase().trim();
  for (let pass = 0; pass < 3; pass += 1) {
    const next = title.replace(VARIANT_MARKERS, "").replace(FEATURE_MARKERS, "").trim();
    if (next === title) break;
    title = next;
  }
  return title.replace(/[^a-z0-9]+/g, " ").trim();
}

export const MAX_ALTERNATE_VERSIONS = 2;

export function normalizedTitle(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

export function hasVersionMarker(name: string): boolean {
  return VARIANT_MARKERS.test(name.toLowerCase());
}

export function isInstrumentalTitle(name: string): boolean {
  return /\binstrumental\b/i.test(name);
}

export function isAlternateVersionOf(seedName: string, otherName: string): boolean {
  const seed = baseTitle(seedName);
  const other = baseTitle(otherName);
  return seed.length > 0 && seed === other && seedName.trim().toLowerCase() !== otherName.trim().toLowerCase();
}

export interface CompanionCandidate {
  spotifyTrackId: string;
  name: string;
  releaseDate: string;
  releaseName?: string;
  features: Partial<AudioFeatureVector> | null;
}

export function isInstrumentalCandidate(candidate: Pick<CompanionCandidate, "name" | "releaseName">): boolean {
  return isInstrumentalTitle(candidate.name) || /\binstrumentals?\b/i.test(candidate.releaseName ?? "");
}

export interface OrderedCompanionTrack {
  spotifyTrackId: string;
  name: string;
  score: number;
  reason: "seed" | "match" | "alternate" | "album" | "mood";
}

export function orderCompanionTracks(input: {
  seed: CompanionCandidate;
  candidates: CompanionCandidate[];
  settings: Pick<CompanionPlaylistSettings, "length" | "includeInstrumentals" | "includeAlternateVersions">;
}): OrderedCompanionTrack[] {
  const { seed, candidates, settings } = input;
  const ordered: OrderedCompanionTrack[] = [{ spotifyTrackId: seed.spotifyTrackId, name: seed.name, score: 1, reason: "seed" }];
  if (!hasUsableFeatures(seed.features)) return ordered;
  const seedFeatures = seed.features;
  const seedBase = baseTitle(seed.name);
  const seedTitle = normalizedTitle(seed.name);

  const scored = candidates
    .filter((candidate) => candidate.spotifyTrackId !== seed.spotifyTrackId)
    .filter((candidate) => normalizedTitle(candidate.name) !== seedTitle)
    .filter((candidate) => hasUsableFeatures(candidate.features))
    .filter((candidate) => settings.includeInstrumentals || !isInstrumentalCandidate(candidate))
    .map((candidate) => {
      const alternate = baseTitle(candidate.name) === seedBase;
      const raw = similarityScore(seedFeatures, candidate.features as AudioFeatureVector);
      return { candidate, alternate, score: alternate ? raw * 0.85 : raw };
    })
    .filter((entry) => settings.includeAlternateVersions || !entry.alternate)
    .sort(
      (left, right) =>
        right.score - left.score ||
        right.candidate.releaseDate.localeCompare(left.candidate.releaseDate) ||
        left.candidate.name.localeCompare(right.candidate.name),
    );

  const seenBases = new Set<string>([seedBase]);
  const seenTitles = new Set<string>([seedTitle]);
  const push = (entry: (typeof scored)[number]) => {
    ordered.push({
      spotifyTrackId: entry.candidate.spotifyTrackId,
      name: entry.candidate.name,
      score: Number(entry.score.toFixed(4)),
      reason: entry.alternate ? "alternate" : "match",
    });
  };

  const seedIsVersion = hasVersionMarker(seed.name) && settings.includeAlternateVersions;
  if (seedIsVersion) {
    const family = scored
      .filter((entry) => entry.alternate)
      .sort((left, right) => {
        const leftOriginal = normalizedTitle(left.candidate.name) === seedBase ? 1 : 0;
        const rightOriginal = normalizedTitle(right.candidate.name) === seedBase ? 1 : 0;
        return rightOriginal - leftOriginal || right.score - left.score;
      });
    for (const entry of family) {
      if (ordered.length >= settings.length) break;
      const title = normalizedTitle(entry.candidate.name);
      if (seenTitles.has(title)) continue;
      seenTitles.add(title);
      push(entry);
    }
  }

  let alternates = 0;
  for (const entry of scored) {
    if (ordered.length >= settings.length) break;
    const title = normalizedTitle(entry.candidate.name);
    if (seenTitles.has(title)) continue;
    if (entry.alternate) {
      if (seedIsVersion || alternates >= MAX_ALTERNATE_VERSIONS) continue;
      alternates += 1;
    } else {
      const base = baseTitle(entry.candidate.name);
      if (seenBases.has(base)) continue;
      seenBases.add(base);
    }
    seenTitles.add(title);
    push(entry);
  }
  return ordered;
}

export function companionSnapshot(input: { trackIds: string[]; name: string; description: string; coverUrl?: string | null }): string {
  const payload = `${input.name}\n${input.description}\n${input.coverUrl ?? ""}\n${input.trackIds.join(",")}`;
  let hash = 5381;
  for (let index = 0; index < payload.length; index += 1) {
    hash = ((hash << 5) + hash + payload.charCodeAt(index)) | 0;
  }
  return `v1:${(hash >>> 0).toString(16)}:${input.trackIds.length}`;
}

export function spotifyTrackUri(spotifyTrackId: string): string {
  return `spotify:track:${spotifyTrackId}`;
}

export function resolveSpotifyHref(input: { fallback?: string | null; companionUrl?: string | null }): string | undefined {
  return input.companionUrl || input.fallback || undefined;
}

export type MoodPlaylistId = "high-energy" | "late-night" | "focus" | "worship" | "christmas" | "instrumentals" | "new-drops";

export interface MoodScoreInput {
  name: string;
  features: AudioFeatureVector | null;
  releaseName?: string;
}

export interface MoodDefinition {
  id: MoodPlaylistId;
  name: string;
  description: string;
  minScore: number;
  requiresFeatures: boolean;
  allowInstrumentals: boolean;
  excludesSeasonal: boolean;
  order: "score" | "recency";
  maxTracks?: number;
  fillToLength?: boolean;
  score: (input: MoodScoreInput) => number;
}

export const NEW_DROPS_LENGTH = 30;

const clamp01 = (value: number) => (Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0);
const rampUp = (value: number, low: number, high: number) => clamp01((value - low) / (high - low));
const rampDown = (value: number, high: number, low: number) => clamp01((high - value) / (high - low));
const centred = (value: number, ideal: number, tolerance: number) => clamp01(1 - Math.abs(value - ideal) / tolerance);
const loudnessLevel = (loudnessDb: number | null | undefined) => (typeof loudnessDb === "number" ? clamp01((loudnessDb + 18) / 12) : 0.5);

const CHRISTMAS_WORDS = [
  "christmas",
  "xmas",
  "noel",
  "navidad",
  "santa",
  "claus",
  "jingle",
  "sleigh",
  "mistletoe",
  "reindeer",
  "rudolph",
  "manger",
  "hark",
  "herald",
  "wenceslas",
  "nativity",
  "tinsel",
  "grinch",
  "frosty",
  "yule",
  "yuletide",
  "merry",
  "silent night",
  "holy night",
  "winter wonderland",
  "joy to the world",
  "little drummer",
  "drummer boy",
  "deck the halls",
  "let it snow",
  "we three kings",
  "come all ye faithful",
  "god rest ye",
  "carol of the bells", "three ships",
  "silver bells",
];

const WORSHIP_WORDS = [
  "worship",
  "praise",
  "hallelujah",
  "holy",
  "lord",
  "jesus",
  "christ",
  "god",
  "pray",
  "prayer",
  "amen",
  "glory",
  "grace",
  "faith",
  "spirit",
  "heaven",
  "blessed",
  "saved",
  "gospel",
  "psalm",
  "hymn",
  "altar",
  "testimony",
];

export function titleMentions(name: string, words: string[]): boolean {
  const normalized = normalizedTitle(name);
  if (!normalized) return false;
  const tokens = new Set(normalized.split(" ").filter(Boolean));
  return words.some((word) => {
    const target = normalizedTitle(word);
    if (!target) return false;
    if (target.includes(" ")) return ` ${normalized} `.includes(` ${target} `);
    return tokens.has(target);
  });
}

export function isSeasonalTitle(name: string): boolean {
  return titleMentions(name, CHRISTMAS_WORDS);
}

export const MOOD_PLAYLISTS: MoodDefinition[] = [
  {
    id: "high-energy",
    name: "High Energy",
    description: "The fastest, loudest and brightest songs in the catalog, picked from the audio itself rather than from their titles.",
    minScore: 0.55,
    requiresFeatures: true,
    fillToLength: true,
    allowInstrumentals: false,
    excludesSeasonal: true,
    order: "score",
    score: ({ features }) => {
      if (!features) return 0;
      return (
        rampUp(features.bpm, 100, 140) * 0.35 +
        clamp01(features.energy) * 0.3 +
        clamp01(features.brightness) * 0.2 +
        loudnessLevel(features.loudnessDb) * 0.15
      );
    },
  },
  {
    id: "late-night",
    name: "Late Night",
    description: "Slow, dark and low on energy, leaning on the songs written in a minor key.",
    minScore: 0.55,
    requiresFeatures: true,
    fillToLength: true,
    allowInstrumentals: false,
    excludesSeasonal: true,
    order: "score",
    score: ({ features }) => {
      if (!features) return 0;
      const minorLean = features.mode === "minor" ? 1 : features.mode === "major" ? 0.3 : 0.6;
      return (
        rampDown(features.bpm, 110, 75) * 0.3 +
        (1 - clamp01(features.energy)) * 0.3 +
        (1 - clamp01(features.brightness)) * 0.25 +
        minorLean * 0.15
      );
    },
  },
  {
    id: "focus",
    name: "Focus",
    description: "Mid tempo, steady energy and little treble, for the hours you need something on in the background.",
    minScore: 0.55,
    requiresFeatures: true,
    fillToLength: true,
    allowInstrumentals: true,
    excludesSeasonal: true,
    order: "score",
    score: ({ features }) => {
      if (!features) return 0;
      return centred(features.bpm, 100, 30) * 0.4 + centred(features.energy, 0.45, 0.45) * 0.35 + (1 - clamp01(features.brightness)) * 0.25;
    },
  },
  {
    id: "worship",
    name: "Worship",
    description: "Songs whose titles say so, ordered by the ones closest to a steady, unhurried tempo.",
    minScore: 0.5,
    requiresFeatures: false,
    fillToLength: true,
    allowInstrumentals: false,
    excludesSeasonal: true,
    order: "score",
    score: ({ name, features }) => {
      if (!titleMentions(name, WORSHIP_WORDS)) return 0;
      if (!features) return 0.5;
      const musical = centred(features.bpm, 88, 38) * 0.6 + centred(features.energy, 0.45, 0.45) * 0.4;
      return 0.5 + musical * 0.5;
    },
  },
  {
    id: "christmas",
    name: "Christmas",
    description: "Every Christmas record in the catalog, newest first.",
    minScore: 1,
    requiresFeatures: false,
    allowInstrumentals: true,
    excludesSeasonal: false,
    order: "recency",
    score: ({ name }) => (titleMentions(name, CHRISTMAS_WORDS) ? 1 : 0),
  },
  {
    id: "instrumentals",
    name: "Instrumentals",
    description: "Every version marked instrumental, newest first.",
    minScore: 1,
    requiresFeatures: false,
    allowInstrumentals: true,
    excludesSeasonal: false,
    order: "recency",
    score: ({ name, releaseName }) => (isInstrumentalCandidate({ name, releaseName }) ? 1 : 0),
  },
  {
    id: "new-drops",
    name: "New Drops",
    description: `The ${NEW_DROPS_LENGTH} newest songs in the catalog, newest first.`,
    minScore: 1,
    requiresFeatures: false,
    allowInstrumentals: false,
    excludesSeasonal: false,
    order: "recency",
    maxTracks: NEW_DROPS_LENGTH,
    score: () => 1,
  },
];

export function moodById(moodId: string): MoodDefinition | undefined {
  return MOOD_PLAYLISTS.find((mood) => mood.id === moodId);
}

export function selectMoodTracks(input: { mood: MoodDefinition; candidates: CompanionCandidate[]; limit: number; fill?: boolean }): OrderedCompanionTrack[] {
  const { mood, candidates } = input;
  const fill = Boolean(input.fill && mood.fillToLength);
  const cap = Math.max(1, Math.min(input.limit, mood.maxTracks ?? input.limit));
  const versionRank = (name: string) => (hasVersionMarker(name) ? 1 : 0);
  const scored = candidates
    .filter((candidate) => mood.allowInstrumentals || !isInstrumentalCandidate(candidate))
    .filter((candidate) => !mood.excludesSeasonal || !isSeasonalTitle(candidate.name))
    .filter((candidate) => !mood.requiresFeatures || hasUsableFeatures(candidate.features))
    .map((candidate) => ({
      candidate,
      score: mood.score({ name: candidate.name, releaseName: candidate.releaseName, features: hasUsableFeatures(candidate.features) ? candidate.features : null }),
    }))
    .filter((entry) => entry.score >= mood.minScore || fill)
    .sort((left, right) =>
      mood.order === "recency"
        ? right.candidate.releaseDate.localeCompare(left.candidate.releaseDate) ||
          versionRank(left.candidate.name) - versionRank(right.candidate.name) ||
          right.score - left.score ||
          left.candidate.name.localeCompare(right.candidate.name)
        : Number(right.score >= mood.minScore) - Number(left.score >= mood.minScore) ||
          right.score - left.score ||
          versionRank(left.candidate.name) - versionRank(right.candidate.name) ||
          right.candidate.releaseDate.localeCompare(left.candidate.releaseDate) ||
          left.candidate.name.localeCompare(right.candidate.name),
    );

  const seen = new Set<string>();
  const seenSongs = new Set<string>();
  const picked: OrderedCompanionTrack[] = [];
  for (const entry of scored) {
    if (picked.length >= cap) break;
    const title = normalizedTitle(entry.candidate.name);
    if (!title || seen.has(title)) continue;
    const song = baseTitle(entry.candidate.name) || title;
    if (seenSongs.has(song)) continue;
    seenSongs.add(song);
    seen.add(title);
    picked.push({
      spotifyTrackId: entry.candidate.spotifyTrackId,
      name: entry.candidate.name,
      score: Number(entry.score.toFixed(4)),
      reason: "mood",
    });
  }
  return picked;
}

export function orderAlbumCompanionTracks(input: {
  albumTracks: CompanionCandidate[];
  candidates: CompanionCandidate[];
  settings: Pick<CompanionPlaylistSettings, "length" | "includeInstrumentals">;
}): OrderedCompanionTrack[] {
  const { albumTracks, candidates, settings } = input;
  const ordered: OrderedCompanionTrack[] = albumTracks.map((track) => ({
    spotifyTrackId: track.spotifyTrackId,
    name: track.name,
    score: 1,
    reason: "album",
  }));
  const albumIds = new Set(albumTracks.map((track) => track.spotifyTrackId));
  const albumTitles = new Set(albumTracks.map((track) => normalizedTitle(track.name)));
  const seeds = albumTracks.filter((track) => hasUsableFeatures(track.features)).map((track) => track.features as AudioFeatureVector);
  if (seeds.length === 0) return ordered;

  const scored = candidates
    .filter((candidate) => !albumIds.has(candidate.spotifyTrackId))
    .filter((candidate) => !albumTitles.has(normalizedTitle(candidate.name)))
    .filter((candidate) => hasUsableFeatures(candidate.features))
    .filter((candidate) => settings.includeInstrumentals || !isInstrumentalCandidate(candidate))
    .map((candidate) => ({
      candidate,
      score: Math.max(...seeds.map((seed) => similarityScore(seed, candidate.features as AudioFeatureVector))),
    }))
    .sort(
      (left, right) =>
        right.score - left.score ||
        right.candidate.releaseDate.localeCompare(left.candidate.releaseDate) ||
        left.candidate.name.localeCompare(right.candidate.name),
    );

  const seen = new Set<string>(albumTitles);
  const limit = albumTracks.length + settings.length;
  for (const entry of scored) {
    if (ordered.length >= limit) break;
    const title = normalizedTitle(entry.candidate.name);
    if (!title || seen.has(title)) continue;
    seen.add(title);
    ordered.push({
      spotifyTrackId: entry.candidate.spotifyTrackId,
      name: entry.candidate.name,
      score: Number(entry.score.toFixed(4)),
      reason: "match",
    });
  }
  return ordered;
}
